import { useEffect, useRef, useState } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FaTimes } from "react-icons/fa";
import { showError, showSuccess } from "../../../../utils/toastUtils";
import uploadFileToCloudinary from "../../../../utils/uploadFileToCloudinary";

const VideoUploadSection = ({
  icon: Icon,
  title,
  description,
  buttonText,
  badge,
  maxSizeMB = 20,
  onFileSelect,
  prefillData,
}) => {
  const fileInputRef = useRef(null);
  const uploadStarted = useRef(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(prefillData?.[0] || null);
  const [uploadController, setUploadController] = useState(null);

  useEffect(() => {
    if (prefillData && prefillData.length > 0) {
      setPreviewUrl(prefillData[0]);
      setVideoUrl(prefillData[0]);
    }
  }, [prefillData]);

  useEffect(() => {
    const handleUpload = async () => {
      if (!selectedFile || isCancelled || uploadStarted.current) return;
      uploadStarted.current = true;

      try {
        setIsUploading(true);
        setUploadProgress(0);
        const { uploadPromise, cancel } = uploadFileToCloudinary(
          selectedFile,
          (p) => setUploadProgress(p)
        );
        setUploadController({ cancel });
        const result = await uploadPromise;

        setIsUploading(false);
        setVideoUrl(result.url);
        setUploadController(null);
        setIsCancelled(false);

        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }

        showSuccess("Video uploaded successfully");
        onFileSelect?.(result.url);
      } catch (err) {
        setIsUploading(false);
        setUploadController(null);
        if (!isCancelled) showError(err.message || "Failed to upload video");
      } finally {
        uploadStarted.current = false;
      }
    };

    handleUpload();
  }, [selectedFile, isCancelled]);

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("video/")) {
        showError("Only video files are allowed!");
        return;
      }
      if (file.size > maxSizeMB * 1024 * 1024) {
        showError(`File size exceeds ${maxSizeMB}MB`);
        return;
      }

      // Create blob URL for immediate preview
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setSelectedFile(file);
      setIsCancelled(false);
    }
  };

  const handleRemovePreview = (e) => {
    e.stopPropagation();
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
    }
    setVideoUrl(null);
    setPreviewUrl(null);
    setSelectedFile(null);
    setUploadProgress(0);
    setIsCancelled(false);
    setUploadController(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCancelUpload = (e) => {
    e.stopPropagation();
    if (uploadController) {
      uploadController.cancel();
      setUploadController(null);
    }
    setIsUploading(false);
    setIsCancelled(true);
  };

  const handleContinueUpload = (e) => {
    e.stopPropagation();
    setIsCancelled(false);
  };

  const displayUrl = videoUrl || previewUrl;

  return (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 text-center hover:border-primary transition-colors cursor-pointer relative">
      {!selectedFile && !displayUrl && (
        <div onClick={handleFileClick}>
          <div className="flex justify-center mb-3">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center relative">
              {Icon && <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />}
              {badge && (
                <span className="absolute -top-1 -right-1 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                  {badge}
                </span>
              )}
            </div>
          </div>

          <input
            type="file"
            accept="video/*"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleFileClick();
            }}
            className="text-primary text-sm sm:text-base font-medium hover:underline"
          >
            {buttonText}
          </button>

          <p className="text-xs text-text-muted mt-2">
            {description || `Max size ${maxSizeMB}MB | Format: Video`}
          </p>
        </div>
      )}

      {displayUrl && (
        <div>
          <div className="relative flex justify-center group">
            <div className="relative rounded-lg overflow-hidden w-full max-w-xs sm:max-w-sm">
              <video
                src={displayUrl}
                controls
                className="rounded-lg border border-gray-200 w-full"
              />

              {/* Upload Progress Overlay */}
              {isUploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div
                    className="absolute inset-0 bg-primary/30"
                    style={{
                      width: `${uploadProgress}%`,
                      transition: "width 0.3s ease",
                    }}
                  />
                  <div className="relative z-10 flex flex-col items-center gap-2">
                    <AiOutlineLoading3Quarters className="w-8 h-8 text-white animate-spin" />
                    <p className="text-white font-semibold text-lg">
                      {uploadProgress}%
                    </p>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={handleRemovePreview}
              className="absolute -top-2 -right-2 p-1.5 bg-black/60 hover:bg-red-600/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <FaTimes className="w-4 h-4 text-white" />
            </button>
          </div>

          <div className="mt-4 flex justify-center">
            {isUploading ? (
              <button
                onClick={handleCancelUpload}
                className="w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Cancel Upload
              </button>
            ) : isCancelled && !videoUrl ? (
              <button
                onClick={handleContinueUpload}
                className="w-full px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Continue Upload
              </button>
            ) : videoUrl ? (
              <button className="w-full px-4 py-2 bg-status-success  text-white rounded-lg text-sm font-medium transition-colors opacity-60 cursor-not-allowed">
                Video Uploaded ✓
              </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoUploadSection;
